/**
 * JobProgressBar — renders a job's latest-run status as a fill bar with a
 * duration / row-count / error summary underneath.
 *
 * Extracted from JobList.tsx so it can be shared by the top-level
 * "Pipeline Runs" tab (PipelineRunsTab), which is where per-job run detail
 * now lives — the Jobs tab itself only handles job management
 * (create/edit/delete).
 */

import type { PEJobRun } from "../../features/pipeline-engineer/types";
import {
  formatDuration,
  formatRelativeTime,
  runStatusStyle,
} from "../../features/pipeline-engineer/runFormatting";

/**
 * Progress-bar width per run status — a fixed visual proxy (queued/running
 * have no real percent-complete signal from the backend, only a status; see
 * PEJobRun) rather than a fabricated exact percentage. Terminal statuses fill
 * the bar; an unknown one leaves it empty.
 */
const PROGRESS_PCT: Record<string, number> = {
  queued: 15,
  running: 65,
  completed: 100,
  failed: 100,
  cancelled: 100,
};

/** Per-job run status, rendered as a fill bar (queued/running/completed/failed/cancelled). */
export function JobProgressBar({ run }: { run: PEJobRun | undefined }) {
  if (!run) {
    return (
      <div className="flex items-center gap-2 max-w-[220px]">
        <div className="flex-1 h-1.5 rounded-full bg-white/5" />
        <span className="text-xs text-white/30 shrink-0">Not run yet</span>
      </div>
    );
  }

  const style = runStatusStyle(run.status);
  const config = {
    pct: PROGRESS_PCT[run.status] ?? 0,
    barClass: style.bar,
    textClass: style.text,
    label: style.label,
    pulse: run.status === "running",
  };

  // Terminal runs report "when" via completedAt (falls back to startedAt for
  // older rows that predate that column); in-flight runs always use startedAt.
  const whenIso =
    (run.status === "completed" ||
      run.status === "failed" ||
      run.status === "cancelled") &&
    run.completedAt
      ? run.completedAt
      : run.startedAt;
  const durationLabel = formatDuration(run.durationSeconds);
  const rowsLabel =
    run.status === "completed" && run.rowsLoaded !== null
      ? `${run.rowsLoaded.toLocaleString()} rows`
      : null;

  return (
    <div className="max-w-[320px] space-y-0.5">
      <div
        className="flex items-center gap-2"
        title={
          run.status === "failed" && run.errorMessage
            ? run.errorMessage
            : config.label
        }
      >
        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden min-w-[60px]">
          <div
            className={`h-full rounded-full transition-all duration-500 ${config.barClass} ${
              config.pulse ? "animate-pulse" : ""
            }`}
            style={{ width: `${config.pct}%` }}
          />
        </div>
        <span className={`text-xs shrink-0 font-medium ${config.textClass}`}>
          {config.label}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-white/35">
        <span>Last run: {formatRelativeTime(whenIso)}</span>
        {durationLabel && <span>· {durationLabel}</span>}
        {rowsLabel && <span>· {rowsLabel}</span>}
      </div>
      {run.status === "failed" && run.errorMessage && (
        <p
          className="text-[11px] text-red-400/80 truncate"
          title={run.errorMessage}
        >
          {run.errorMessage}
        </p>
      )}
    </div>
  );
}
